import { useState, useEffect } from 'react';

const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD || 'PLGAdmin2026!';

const PLAN_PRICES = {
  free:    0,
  starter: 49,
  pro:     99,
  premium: 300,
};

const PLAN_COLORS = {
  free:    '#64748b',
  starter: '#6366f1',
  pro:     '#7c3aed',
  premium: '#059669',
};

export default function AdminDashboard() {
  const [authed,    setAuthed]    = useState(false);
  const [password,  setPassword]  = useState('');
  const [error,     setError]     = useState('');
  const [users,     setUsers]     = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [search,    setSearch]    = useState('');
  const [planFilter,setPlanFilter]= useState('all');
  const [sortBy,    setSortBy]    = useState('newest');

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setAuthed(true);
      setError('');
    } else {
      setError('Incorrect password. Try again.');
    }
  };

  useEffect(() => {
    if (!authed) return;
    fetchUsers();
  }, [authed]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/admin-users?password=${encodeURIComponent(ADMIN_PASSWORD)}`);
      const data = await response.json();

      if (!response.ok) {
        console.error('Admin API error:', data);
        setUsers([]);
      } else {
        setUsers(data.users || []);
      }
    } catch (err) {
      console.error('Admin fetch error:', err);
      setUsers([]);
    }
    setLoading(false);
  };

  // ── Stats ──────────────────────────────────────────────────────
  const totalUsers    = users.length;
  const paidUsers     = users.filter(u => u.plan !== 'free').length;
  const totalRevenue  = users.reduce((sum, u) => sum + (PLAN_PRICES[u.plan] || 0), 0);
  const convRate      = totalUsers > 0 ? Math.round((paidUsers / totalUsers) * 100) : 0;
  const planCounts    = users.reduce((acc, u) => { acc[u.plan] = (acc[u.plan]||0)+1; return acc; }, {});

  // ── Filter + Sort ──────────────────────────────────────────────
  const filtered = users
    .filter(u => {
      const matchSearch = !search ||
        u.email?.toLowerCase().includes(search.toLowerCase()) ||
        u.name?.toLowerCase().includes(search.toLowerCase());
      const matchPlan = planFilter === 'all' || u.plan === planFilter;
      return matchSearch && matchPlan;
    })
    .sort((a, b) => {
      if (sortBy === 'newest') return new Date(b.created_at) - new Date(a.created_at);
      if (sortBy === 'oldest') return new Date(a.created_at) - new Date(b.created_at);
      if (sortBy === 'plan')   return (b.plan||'').localeCompare(a.plan||'');
      return 0;
    });

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleTimeString('en-GB', {
      hour: '2-digit', minute: '2-digit',
    });
  };

  // ── Login screen ───────────────────────────────────────────────
  if (!authed) {
    return (
      <div style={{ minHeight:'100vh', background:'linear-gradient(160deg,#0f172a,#1e293b)', display:'flex', alignItems:'center', justifyContent:'center', padding:24 }}>
        <div style={{ background:'#1e293b', borderRadius:20, border:'1px solid #334155', padding:'48px 40px', maxWidth:420, width:'100%', textAlign:'center' }}>
          <div style={{ fontSize:48, marginBottom:16 }}>🔐</div>
          <h1 style={{ fontSize:24, fontWeight:800, color:'#f8fafc', marginBottom:8, fontFamily:'sans-serif' }}>Admin Access</h1>
          <p style={{ fontSize:14, color:'#94a3b8', fontFamily:'sans-serif', marginBottom:32 }}>PilotLeadGen Dashboard</p>
          {error && <div style={{ background:'#450a0a', border:'1px solid #dc2626', borderRadius:10, padding:'12px', fontSize:13, color:'#fca5a5', fontFamily:'sans-serif', marginBottom:20 }}>{error}</div>}
          <form onSubmit={handleLogin}>
            <input
              type="password"
              placeholder="Enter admin password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              style={{ width:'100%', padding:'14px 16px', borderRadius:12, border:'1px solid #334155', background:'#0f172a', color:'#f8fafc', fontSize:15, fontFamily:'sans-serif', marginBottom:16, boxSizing:'border-box' }}
            />
            <button type="submit" style={{ width:'100%', padding:'14px', borderRadius:12, background:'linear-gradient(135deg,#6366f1,#818cf8)', color:'#fff', border:'none', fontSize:15, fontWeight:700, cursor:'pointer', fontFamily:'sans-serif' }}>
              Enter Dashboard →
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ── Dashboard ──────────────────────────────────────────────────
  return (
    <div style={{ minHeight:'100vh', background:'#f8faff', fontFamily:'sans-serif' }}>

      {/* Header */}
      <div style={{ background:'linear-gradient(135deg,#0f172a,#1e293b)', padding:'24px 32px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div>
          <div style={{ fontSize:13, fontWeight:700, color:'#6366f1', textTransform:'uppercase', letterSpacing:1, marginBottom:4 }}>PilotLeadGen</div>
          <h1 style={{ fontSize:22, fontWeight:800, color:'#f8fafc', margin:0 }}>Admin Dashboard</h1>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <button onClick={fetchUsers} style={{ padding:'8px 16px', borderRadius:10, background:'#1e293b', border:'1px solid #334155', color:'#94a3b8', fontSize:13, cursor:'pointer' }}>
            🔄 Refresh
          </button>
          <button onClick={() => setAuthed(false)} style={{ padding:'8px 16px', borderRadius:10, background:'#dc2626', border:'none', color:'#fff', fontSize:13, cursor:'pointer', fontWeight:600 }}>
            Logout
          </button>
        </div>
      </div>

      <div style={{ padding:'32px', maxWidth:1200, margin:'0 auto' }}>

        {/* Stats cards */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:16, marginBottom:32 }}>
          {[
            { label:'Total Users',     value:totalUsers,         icon:'👥', color:'#6366f1', sub:'All signups'           },
            { label:'Paid Users',      value:paidUsers,          icon:'💰', color:'#059669', sub:'Starter + Pro + Premium'},
            { label:'Total Revenue',   value:`$${totalRevenue}`, icon:'💵', color:'#7c3aed', sub:'Estimated lifetime'     },
            { label:'Conversion Rate', value:`${convRate}%`,     icon:'📈', color:'#0891b2', sub:'Free → Paid'           },
          ].map((stat, i) => (
            <div key={i} style={{ background:'#fff', borderRadius:16, border:'1px solid #e2e8f0', padding:'20px 24px', boxShadow:'0 2px 8px rgba(0,0,0,0.04)' }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
                <span style={{ fontSize:24 }}>{stat.icon}</span>
                <span style={{ fontSize:11, fontWeight:700, color:stat.color, background:`${stat.color}15`, padding:'3px 10px', borderRadius:20, textTransform:'uppercase', letterSpacing:0.5 }}>LIVE</span>
              </div>
              <div style={{ fontSize:28, fontWeight:800, color:'#0f172a', marginBottom:4 }}>{stat.value}</div>
              <div style={{ fontSize:12, fontWeight:700, color:'#1e293b', marginBottom:2 }}>{stat.label}</div>
              <div style={{ fontSize:11, color:'#94a3b8' }}>{stat.sub}</div>
            </div>
          ))}
        </div>

        {/* Plan breakdown */}
        <div style={{ background:'#fff', borderRadius:16, border:'1px solid #e2e8f0', padding:'24px', marginBottom:24, boxShadow:'0 2px 8px rgba(0,0,0,0.04)' }}>
          <h2 style={{ fontSize:16, fontWeight:800, color:'#0f172a', margin:'0 0 20px' }}>Plan Breakdown</h2>
          <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
            {['free','starter','pro','premium'].map(plan => (
              <div key={plan} style={{ flex:1, minWidth:120, background:'#f8faff', borderRadius:12, border:`2px solid ${PLAN_COLORS[plan]}25`, padding:'16px 20px', textAlign:'center' }}>
                <div style={{ fontSize:24, fontWeight:800, color:PLAN_COLORS[plan] }}>{planCounts[plan] || 0}</div>
                <div style={{ fontSize:12, fontWeight:700, color:'#475569', textTransform:'capitalize', marginTop:4 }}>{plan}</div>
                <div style={{ fontSize:11, color:'#94a3b8', marginTop:2 }}>
                  {totalUsers > 0 ? Math.round(((planCounts[plan]||0)/totalUsers)*100) : 0}%
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Filters */}
        <div style={{ background:'#fff', borderRadius:16, border:'1px solid #e2e8f0', padding:'16px 24px', marginBottom:16, display:'flex', gap:12, flexWrap:'wrap', alignItems:'center', boxShadow:'0 2px 8px rgba(0,0,0,0.04)' }}>
          <input
            placeholder="🔍 Search by email or name..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ flex:1, minWidth:200, padding:'10px 14px', borderRadius:10, border:'1px solid #e2e8f0', fontSize:13, color:'#334155', outline:'none' }}
          />
          <select value={planFilter} onChange={e => setPlanFilter(e.target.value)} style={{ padding:'10px 14px', borderRadius:10, border:'1px solid #e2e8f0', fontSize:13, color:'#334155', cursor:'pointer' }}>
            <option value="all">All Plans</option>
            <option value="free">Free</option>
            <option value="starter">Starter</option>
            <option value="pro">Pro</option>
            <option value="premium">Premium</option>
          </select>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{ padding:'10px 14px', borderRadius:10, border:'1px solid #e2e8f0', fontSize:13, color:'#334155', cursor:'pointer' }}>
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="plan">By Plan</option>
          </select>
          <div style={{ fontSize:13, color:'#64748b', whiteSpace:'nowrap' }}>
            {filtered.length} of {totalUsers} users
          </div>
        </div>

        {/* Users table */}
        <div style={{ background:'#fff', borderRadius:16, border:'1px solid #e2e8f0', overflow:'hidden', boxShadow:'0 2px 8px rgba(0,0,0,0.04)' }}>
          {loading ? (
            <div style={{ padding:60, textAlign:'center', color:'#94a3b8', fontSize:14 }}>Loading users...</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding:60, textAlign:'center', color:'#94a3b8', fontSize:14 }}>No users found.</div>
          ) : (
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead>
                  <tr style={{ background:'#f8faff', borderBottom:'1px solid #e2e8f0' }}>
                    {['#','Name','Email','Plan','Revenue','Country','City','Signed Up','Last Login'].map(h => (
                      <th key={h} style={{ padding:'14px 16px', textAlign:'left', fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:0.5, whiteSpace:'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((user, i) => (
                    <tr key={user.id || i} style={{ borderBottom:'1px solid #f1f5f9', transition:'background 0.1s' }}
                      onMouseEnter={e => e.currentTarget.style.background='#f8faff'}
                      onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                      <td style={{ padding:'14px 16px', fontSize:13, color:'#94a3b8', fontWeight:600 }}>{i+1}</td>
                      <td style={{ padding:'14px 16px', fontSize:13, fontWeight:700, color:'#1e293b' }}>{user.name || '—'}</td>
                      <td style={{ padding:'14px 16px', fontSize:13, color:'#475569' }}>{user.email || user.user_id || '—'}</td>
                      <td style={{ padding:'14px 16px' }}>
                        <span style={{ fontSize:12, fontWeight:700, color:PLAN_COLORS[user.plan]||'#64748b', background:`${PLAN_COLORS[user.plan]||'#64748b'}15`, padding:'4px 12px', borderRadius:20, textTransform:'capitalize' }}>
                          {user.plan || 'free'}
                        </span>
                      </td>
                      <td style={{ padding:'14px 16px', fontSize:13, fontWeight:700, color:'#059669' }}>
                        {PLAN_PRICES[user.plan] > 0 ? `$${PLAN_PRICES[user.plan]}` : '—'}
                      </td>
                      <td style={{ padding:'14px 16px', fontSize:12, color:'#64748b', textTransform:'capitalize' }}>
                        {user.country || '—'}
                      </td>
                      <td style={{ padding:'14px 16px', fontSize:12, color:'#64748b', textTransform:'capitalize' }}>
                        {user.city || '—'}
                      </td>
                      <td style={{ padding:'14px 16px' }}>
                        <div style={{ fontSize:13, color:'#334155', fontWeight:600 }}>{formatDate(user.created_at)}</div>
                        <div style={{ fontSize:11, color:'#94a3b8', marginTop:2 }}>{formatTime(user.created_at)}</div>
                      </td>
                      <td style={{ padding:'14px 16px' }}>
                        <div style={{ fontSize:13, color:'#334155' }}>{formatDate(user.last_sign_in_at || user.updated_at)}</div>
                        <div style={{ fontSize:11, color:'#94a3b8', marginTop:2 }}>{formatTime(user.last_sign_in_at || user.updated_at)}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p style={{ textAlign:'center', fontSize:12, color:'#cbd5e1', marginTop:24 }}>
          PilotLeadGen Admin · Data from Supabase · Refreshed on page load
        </p>
      </div>
    </div>
  );
}